'use client'

import {
  ArtistEditorialPageDetailsFragment,
  ArtistPageProjectFragment,
  Categories_Enum,
  Verticals_Enum
} from '@/gql/generated/graphql'
import { MarkdownDisplay } from '@components/markdown-text'
import { BodyText, DetailText, DisplayText } from 'design-configuration/components'
import { ImageWithCustomLoader } from '@components/image-with-custom-loader'
import React, { Suspense } from 'react'
import { SocialLinks } from '../social-links'
import { PressSection } from '../press-section'
import { ExhibitionsSection } from '../exhibitions-section'
import { ArticlesSection } from '../articles-section'
import { SimilarArtists } from '../similar-artists'
import { ProjectCard } from '@/app/discover/components/project-card'
import { OverlineHeader } from '@components/overline-header'

type ArtistDisplayProps = {
  individualArtistData: ArtistEditorialPageDetailsFragment | undefined
  walletProjectData: ArtistPageProjectFragment[] | undefined
}
export enum ArtistTemplateSections {
  Releases = 'Art Blocks collections',
  Articles = 'From the Journal',
  Exhibitions = 'Other notable works',
  Press = 'In the news'
}

type ArtistTemplateSectionProps = {
  section: string
  artistInfo?: ArtistEditorialPageDetailsFragment | null
}

const RenderSections = ({ section, artistInfo }: ArtistTemplateSectionProps): React.ReactElement => {
  switch (section) {
    case ArtistTemplateSections.Articles:
      return <ArticlesSection artistInfo={artistInfo} />
    case ArtistTemplateSections.Exhibitions:
      return <ExhibitionsSection artistInfo={artistInfo} />
    case ArtistTemplateSections.Press:
      return <PressSection artistInfo={artistInfo} />
    default:
      console.error(`Section ${section} not found`)
      return <></>
  }
}

export const ArtistDisplay = ({ individualArtistData, walletProjectData }: ArtistDisplayProps): React.ReactElement => {
  const artist = individualArtistData

  const curatedAndCollabProjects =
    artist?.ReleasesSection?.projectMetadata?.filter(
      (p) =>
        p?.Project?.vertical_name === Verticals_Enum.Curated ||
        p?.Project?.vertical?.category_name === Categories_Enum.Collaborations
    ) ?? []

  // Deduplicate projects
  const projects = [
    ...(artist?.ReleasesSection?.projectMetadata?.map((p) => p?.Project) ?? []),
    ...(walletProjectData ?? [])
  ].filter((project, index, self) => project && index === self.findIndex((p) => p?.id === project.id))

  const ALL_SECTIONS = [
    {
      overline: 'EDITORIAL',
      label: ArtistTemplateSections.Articles,
      data: artist?.SpectrumArticlesSection?.SpectrumArticle
    },
    {
      overline: 'SPOTLIGHT',
      label: ArtistTemplateSections.Exhibitions,
      data: artist?.ExhibitionsSection?.exhibitionItem
    },
    {
      overline: 'PRESS',
      label: ArtistTemplateSections.Press,
      data: artist?.PressSection?.PressItem
    }
  ]

  const sections = []
  const overlines: Record<string, string> = {}

  for (let i = 0; i < ALL_SECTIONS.length; i++) {
    const { overline, label, data } = ALL_SECTIONS[i]
    if (data?.length) {
      sections.push(label)
      overlines[label] = overline
    }
  }

  const tags = curatedAndCollabProjects
    .map((c) => {
      const projectTags = c?.Project?.tags.map((t) => t.tag_name || '')
      return projectTags ?? ''
    })
    .flat()
  return (
    <div>
      {/* header */}
      <div className="mt-nav-bar-height relative flex w-full flex-col lg:min-h-[80dvh] lg:flex-row">
        <div className="lg:aspect-none relative aspect-video w-full lg:w-1/2">
          <ImageWithCustomLoader
            src={
              artist?.headerImage?.data?.attributes?.url ||
              artist?.ReleasesSection?.projectMetadata?.[0]?.Project?.featured_token?.[0]?.media_url ||
              ''
            }
            style={{
              objectFit: 'cover',
              objectPosition: 'center'
            }}
            fill
            alt={`Header photo for artist: ${artist?.artistName}`}
            sizes="100vw,50vw"
          />
        </div>
        <div className="bg-surface-inverted gap-md text-on-surface-inverted py-2xl px-md lg:px-2xl z-20 flex w-full flex-col justify-center lg:w-1/2">
          <div>
            <DetailText className="text-on-surface-variant font-medium">FEATURED ARTIST</DetailText>
            <DisplayText className="break-words font-medium">{artist?.artistName}</DisplayText>
          </div>
          <MarkdownDisplay
            className="w-full whitespace-pre-wrap lg:w-[616px]"
            componentOverrides={{
              p: (props) => <BodyText>{props.children}</BodyText>
            }}
          >
            {artist?.bio}
          </MarkdownDisplay>
          <SocialLinks
            artistPage={artist}
            instagram={artist?.instagram}
            twitter={artist?.twitter}
            website={artist?.website}
          />
        </div>
      </div>
      <div className="relative snap-start snap-always">
        {/* ghost element to turn snapping off */}
        <div className="gap-y-2xl pt-2xl px-md md:px-2xl pb-2xl flex h-full w-full flex-col md:gap-y-[80px] md:pb-[80px]">
          {/* other projects section */}
          {projects.length ? (
            <div>
              <OverlineHeader text="RELEASES" />
              <DisplayText className="mb-md">Art Blocks collections</DisplayText>
              <div className="md:gap-md gap-xl grid md:grid-cols-3">
                {projects.map((project) => {
                  if (!project) {
                    return null
                  }
                  return <ProjectCard key={project?.id} project={project} />
                })}
              </div>
            </div>
          ) : null}
          {sections.map((section) => (
            <div key={section}>
              <OverlineHeader text={overlines[section]} />
              <DisplayText className="mb-md">{section}</DisplayText>
              <RenderSections section={section} artistInfo={artist} />
            </div>
          ))}
        </div>
        <Suspense>
          <SimilarArtists tags={tags || []} artistAddress={artist?.userProfileAddress} currArtistSlug={artist?.slug} />
        </Suspense>
      </div>
    </div>
  )
}
